import React, { useState } from 'react';
import {
  CheckStatus, FlightReport, FlightReportCheck, LogChartsResponse, LogMessage, Severity, SEVERITY_CLASSES,
} from '../types';
import { FOCUS, MONO, SEVERITY_TONE } from '../styles';
import Tag from '../../components/ui/Tag';
import Tooltip from '../../components/ui/Tooltip';
import Alert from '../../components/ui/Alert';
import Skeleton from '../../components/ui/Skeleton';
import FadeIn from '../../components/ui/FadeIn';
import Reveal from '../../components/ui/Reveal';
import { DataTable } from '../../components/table/DataTable';
import type { ColumnDef } from '@tanstack/react-table';
import {
  PANEL, PANEL_BADGE, PANEL_HEADER, PANEL_TITLE,
} from './panelChrome';

const MSG_LEVEL_SEVERITY: Record<string, Severity> = {
  Emergency: 'critical', Alert: 'critical', Critical: 'critical',
  Error: 'critical', Warning: 'warning', Notice: 'advisory',
  Info: 'info', Debug: 'info',
};

interface PreAnalysisPanelProps {
  logId: string;
  charts: LogChartsResponse | null;
  chartsLoading: boolean;
  chartsError: string;
  report: FlightReport | null;
  reportLoading: boolean;
  onPickTime: (t: number | null) => void;
}

/* ── Helpers ── */
function formatDuration(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

type LoggedMessage = LogMessage;

const MESSAGE_COLUMNS: ColumnDef<LoggedMessage, any>[] = [
  {
    id: 'time',
    header: 'Time',
    meta: { numeric: true, cellClass: 'w-16 whitespace-nowrap align-top font-mono text-on-surface-variant' },
    cell: ({ row }) => `T+${formatDuration(row.original.timestamp_s)}`,
  },
  {
    id: 'level',
    header: 'Level',
    meta: { cellClass: 'w-16 whitespace-nowrap align-top font-semibold' },
    cell: ({ row }) => (
      <span className={SEVERITY_CLASSES[MSG_LEVEL_SEVERITY[row.original.level] || 'info'].text}>{row.original.level}</span>
    ),
  },
  {
    id: 'message',
    header: 'Message',
    meta: { cellClass: 'break-words font-mono text-on-surface' },
    cell: ({ row }) => row.original.message,
  },
];

/* ── Flight report ── */
const CHECK_HUE: Record<CheckStatus, Severity> = {
  FAIL: 'critical', WARN: 'warning', UNKNOWN: 'info', PASS: 'info',
};

const DISCLOSURE_SUMMARY =
  `np-state flex cursor-pointer list-none items-center gap-1.5 px-3 py-1.5 text-xs text-on-surface-variant [&::-webkit-details-marker]:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40`;

const DisclosureChevron: React.FC = () => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    className="h-3.5 w-3.5 shrink-0 transition-transform duration-200 ease-emphasized group-open:rotate-180 motion-reduce:transition-none"
  >
    <path fill="currentColor" d="M3.5 6h9L8 10.5z" />
  </svg>
);

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

interface FlightReportCardProps {
  report: FlightReport | null;
  loading: boolean;
  onPickTime?: (t: number) => void;
}

const FlightReportCard: React.FC<FlightReportCardProps> = ({ report, loading, onPickTime }) => {
  if (loading) {
    return (
      <section className={`${PANEL} mb-2`} aria-busy="true">
        <div className={PANEL_HEADER}>
          <h3 className={PANEL_TITLE}>Flight report</h3>
          <span className={PANEL_BADGE}>Can take a minute the first time</span>
        </div>
        <p className="sr-only" role="status">Building the flight report</p>
        <div className="flex items-center gap-2 px-3 py-2.5" aria-hidden="true">
          <Skeleton className="h-4 w-10" />
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 max-w-[50%] flex-1" />
        </div>
      </section>
    );
  }
  if (!report) return null;

  const counts = {
    FAIL: report.counts?.FAIL ?? 0,
    WARN: report.counts?.WARN ?? 0,
    PASS: report.counts?.PASS ?? 0,
    UNKNOWN: report.counts?.UNKNOWN ?? 0,
  };
  const checks = report.checks || [];
  const loud: FlightReportCheck[] = [
    ...checks.filter(c => c.status === 'FAIL'),
    ...checks.filter(c => c.status === 'WARN'),
  ];
  const unknown = checks.filter(c => c.status === 'UNKNOWN');
  const fleetLines = report.fleet?.lines || [];

  return (
    <section className={`${PANEL} mb-2`}>
      <div className={PANEL_HEADER}>
        <h3 className={PANEL_TITLE}>Flight report</h3>
        <span className={PANEL_BADGE}>
          {counts.FAIL} FAIL, {counts.WARN} WARN, {counts.PASS} PASS, {counts.UNKNOWN} UNKNOWN
        </span>
      </div>

      {loud.length > 0 ? (
        <ul className="flex flex-col py-1">
          {loud.map((c, i) => (
            <li key={`${c.name}-${i}`} className="flex min-w-0 items-baseline gap-2 px-3 py-1">
              <Tag tone={SEVERITY_TONE[CHECK_HUE[c.status]]} className="shrink-0">
                {c.status}
              </Tag>
              <span className="min-w-0 flex-1 break-words text-sm text-on-surface">
                <span className={`${MONO} mr-2 text-xs text-on-surface-variant`}>{c.name}</span>
                {c.value}
              </span>
              {typeof c.t === 'number' && (
                <Tooltip label="Show where the drone was at this moment">
                  <button
                    type="button"
                    onClick={() => onPickTime?.(c.t as number)}
                    className={`-mx-1 whitespace-nowrap rounded px-1 text-xs tabular-nums text-on-surface-variant hover:text-on-surface hover:underline ${FOCUS}`}
                  >
                    at {c.t.toFixed(1)} s
                  </button>
                </Tooltip>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-3 py-2 text-sm text-on-surface-variant">
          Nothing failed or warned. {plural(counts.PASS, 'check')} passed
          {counts.UNKNOWN > 0 ? `, ${counts.UNKNOWN} could not be judged.` : '.'}
        </p>
      )}

      {unknown.length > 0 && (
        <details className="group">
          <summary className={DISCLOSURE_SUMMARY}>
            <span className="tabular-nums">{unknown.length} UNKNOWN</span>
            <DisclosureChevron />
          </summary>
          <ul className="flex flex-col pb-1.5">
            {unknown.map((c, i) => (
              <li key={`${c.name}-${i}`} className="min-w-0 break-words px-3 py-0.5 text-xs text-on-surface-variant">
                <span className={`${MONO} mr-2`}>{c.name}</span>
                <span className="text-on-surface">{c.detail || c.value}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {fleetLines.length > 0 && (
        <>
          {report.fleet?.note && (
            <p className="px-3 pt-1 text-xs text-on-surface-variant">{report.fleet.note}</p>
          )}
          <details className="group">
            <summary className={DISCLOSURE_SUMMARY}>
              <span>Compared with the fleet</span>
              <DisclosureChevron />
            </summary>
            <ul className={`${MONO} flex flex-col pb-1.5 text-xs text-on-surface`}>
              {fleetLines.map((line, i) => (
                <li key={i} className="min-w-0 whitespace-pre-wrap break-words px-3 py-0.5">{line}</li>
              ))}
            </ul>
          </details>
        </>
      )}
    </section>
  );
};

/* ── Component ── */
const PreAnalysisPanel: React.FC<PreAnalysisPanelProps> = ({
  logId, charts, chartsLoading, chartsError, report, reportLoading, onPickTime,
}) => {
  const [showMessages, setShowMessages] = useState(false);

  if (!logId) {
    return (
      <FadeIn key="nolog" className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1 items-center justify-center p-6">
          <p className="max-w-xs text-center text-sm text-on-surface-variant">
            Pick one flight from the list to see its report.
          </p>
        </div>
      </FadeIn>
    );
  }

  const reportCard = (
    <FadeIn key={reportLoading ? 'loading' : 'ready'}>
      <FlightReportCard report={report} loading={reportLoading} onPickTime={onPickTime} />
    </FadeIn>
  );

  if (chartsLoading) {
    return (
      <FadeIn key="charts" className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-3 py-2.5">
        {reportCard}
        <p className="sr-only" role="status">Reading the log</p>
        <section className={`${PANEL} mb-2`} aria-busy="true">
          <div className={PANEL_HEADER}>
            <span className="text-sm font-semibold text-on-surface-variant">Reading the log</span>
          </div>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(8.75rem,1fr))] gap-2 p-3" aria-hidden="true">
            {[0, 1, 2, 3, 4].map(i => (
              <div key={i} className="flex flex-col gap-1.5">
                <Skeleton className="h-2.5 w-14" />
                <Skeleton className="h-3.5 w-24" />
              </div>
            ))}
          </div>
        </section>
      </div>
      </FadeIn>
    );
  }

  if (chartsError || !charts) {
    return (
      <FadeIn key="charts" className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-3 py-2.5">
        {reportCard}
        {chartsError && (
          <Alert className="mb-2">
            Could not read this flight. {chartsError} Pick it again in the list to retry.
          </Alert>
        )}
      </div>
      </FadeIn>
    );
  }

  const { info, logged_messages } = charts;
  const errorMessages = logged_messages.filter(m =>
    m.level === 'Error' || m.level === 'Critical' || m.level === 'Emergency' || m.level === 'Alert'
  );
  const warningMessages = logged_messages.filter(m => m.level === 'Warning');

  return (
    <FadeIn key="charts" className="flex min-h-0 min-w-0 flex-1 flex-col">
    <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-3 py-2.5">
      {reportCard}

      {/* ── Flight info ── */}
      <section className={`${PANEL} mb-2`}>
        <div className={PANEL_HEADER}>
          <h3 className={PANEL_TITLE}>Flight info</h3>
          <span className={`${PANEL_BADGE} font-mono`}>{info.serial || 'No serial'}</span>
        </div>
        <dl className="grid grid-cols-[repeat(auto-fill,minmax(8.75rem,1fr))] gap-x-2 gap-y-3 p-3">
          {[
            { label: 'Duration', value: formatDuration(info.duration_s) },
            { label: 'Firmware', value: info.firmware || '\u2014' },
            { label: 'Hardware', value: info.hardware || '\u2014' },
            { label: 'Serial', value: info.serial || '\u2014' },
            { label: 'Flight modes', value: info.flight_modes.join(', ') || '\u2014' },
          ].map(item => (
            <div key={item.label} className="min-w-0">
              <dt className="mb-0.5 text-xs text-on-surface-variant">{item.label}</dt>
              <dd className="truncate font-mono text-sm tabular-nums text-on-surface" title={item.value}>
                {item.value}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      {/* ── Logged messages ── */}
      <section className={`${PANEL} mb-2`}>
        <button
          type="button"
          onClick={() => setShowMessages(!showMessages)}
          aria-expanded={showMessages}
          className={`${PANEL_HEADER} np-state w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40`}
        >
          <span className="flex min-w-0 items-baseline gap-3">
            <span className={PANEL_TITLE}>Logged messages</span>
            {errorMessages.length > 0 && (
              <span className={`shrink-0 text-xs font-semibold tabular-nums ${SEVERITY_CLASSES.critical.text}`}>
                {errorMessages.length} error{errorMessages.length > 1 ? 's' : ''}
              </span>
            )}
            {warningMessages.length > 0 && (
              <span className={`shrink-0 text-xs font-semibold tabular-nums ${SEVERITY_CLASSES.warning.text}`}>
                {warningMessages.length} warning{warningMessages.length > 1 ? 's' : ''}
              </span>
            )}
          </span>
          <span className={`${PANEL_BADGE} flex items-center gap-1.5`}>
            {logged_messages.length} messages
            <svg
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
              className={`h-3.5 w-3.5 transition-transform duration-200 ease-emphasized motion-reduce:transition-none ${showMessages ? 'rotate-180' : ''}`}
            >
              <path fill="currentColor" d="M3.5 6h9L8 10.5z" />
            </svg>
          </span>
        </button>
        <Reveal show={showMessages}>
          <div>
            {logged_messages.length === 0 ? (
              <p className="p-3 text-center text-sm text-on-surface-variant">
                The flight logged no messages.
              </p>
            ) : (
              <DataTable<LoggedMessage>
                data={logged_messages}
                columns={MESSAGE_COLUMNS}
                getRowId={(_, i) => String(i)}
                on="panel"
                density="compact"
                striped
                hideHeader
                maxHeight="200px"
                empty={null}
              />
            )}
          </div>
        </Reveal>
      </section>
    </div>
    </FadeIn>
  );
};

export default PreAnalysisPanel;
